import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, AppBar, Toolbar, Container, alpha, Stack } from '@mui/material';
// hooks
import useOffSetTop from '../../hooks/useOffSetTop';
import useResponsive from '../../hooks/useResponsive';
// utils
import cssStyles from '../../utils/cssStyles';
// config
import { HEADER } from '../../config';
// components
import Logo from '../../components/Logo';
//
import MenuDesktop from './MenuDesktop';
import MenuMobile from './MenuMobile';
import { menuConfig as navConfig, secondaryNav } from './MenuConfig';

// ----------------------------------------------------------------------

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  height: HEADER.MOBILE_HEIGHT,
  [theme.breakpoints.up('md')]: {
    backgroundColor: alpha('#FFF', 1),
    height: HEADER.MAIN_DESKTOP_HEIGHT,
    transition: 'height 0.8s',
  },
}));

const ToolbarShadowStyle = styled('div')(({ theme }) => ({
  left: 0,
  right: 0,
  bottom: 0,
  height: 24,
  zIndex: -1,
  margin: 'auto',
  borderRadius: '50%',
  position: 'absolute',
  width: `calc(100% - 48px)`,
  boxShadow: theme.customShadows.z8,
}));

export default function MainHeader() {
  const isOffset = useOffSetTop(HEADER.MAIN_DESKTOP_HEIGHT);

  const theme = useTheme();

  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'md');

  const isHome = pathname === '/';
  const [translateReady, setTranslateReady] = useState(false);

  useEffect(() => {
    const translateElement = document.getElementById('google_translate_element');

    if (!translateElement) {
      return undefined;
    }

    setTranslateReady(false);

    const ensureTranslateSelect = () => {
      let select = translateElement.querySelector('.goog-te-combo');

      if (!select && window.google?.translate?.TranslateElement) {
        translateElement.replaceChildren();
        window.google.translate.TranslateElement(
          {
            pageLanguage: 'fr',
            autoDisplay: false,
          },
          'google_translate_element'
        );
        select = translateElement.querySelector('.goog-te-combo');
      }

      if (select?.options[0]) {
        if (select.options[0].text !== 'Langue') {
          select.options[0].text = 'Langue';
        }
        setTranslateReady(true);
      }

      return Boolean(select);
    };

    ensureTranslateSelect();

    const observer = new MutationObserver(ensureTranslateSelect);
    observer.observe(translateElement, { childList: true, subtree: true });

    let attempts = 0;
    const retryTimer = window.setInterval(() => {
      attempts += 1;

      if (ensureTranslateSelect() || attempts >= 20) {
        window.clearInterval(retryTimer);
      }
    }, 300);

    return () => {
      observer.disconnect();
      window.clearInterval(retryTimer);
    };
  }, [isDesktop]);

  return (
    <AppBar>
      <ToolbarStyle
        disableGutters
        sx={{
          ...(isOffset && {
            ...cssStyles(theme).bgBlur(),
            height: { md: HEADER.MAIN_DESKTOP_HEIGHT - 10 },
          }),
        }}
      >
        <Container
          sx={{
            display: 'flex',
            alignItems: isOffset ? 'center' : 'start',
            justifyContent: { xs: 'space-between' },
            pl: { md: 5 },
          }}
          maxWidth="xl"
        >
          <Logo sx={{ height: 80, width: 120 }}/>
          <Box sx={{ width: 15 }} />
          {isDesktop && (
            <MenuDesktop isOffset={isOffset} isHome={isHome} navConfig={navConfig} secondaryNav={secondaryNav} />
          )}
          {isDesktop && (
            <Stack alignSelf="center" justifyContent="center" sx={{ minWidth: 170 }}>
              {!translateReady && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 170,
                    height: 38,
                    px: 1.5,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    bgcolor: 'common.white',
                    color: 'text.primary',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Langue
                </Box>
              )}
              <Box
                id="google_translate_element"
                sx={{
                  display: translateReady ? 'block' : 'none',
                  '& .goog-te-gadget': {
                    height: 38,
                    fontSize: 0,
                    color: 'transparent',
                  },
                  '& .goog-te-gadget span, & .goog-logo-link': { display: 'none' },
                  '& .goog-te-combo': {
                    width: '100%',
                    minWidth: 170,
                    height: 38,
                    m: 0,
                    px: 1.5,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    bgcolor: 'common.white',
                    color: 'text.primary',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    outline: 'none',
                    '&:hover, &:focus': {
                      borderColor: 'primary.dark',
                      boxShadow: (currentTheme) => `0 0 0 2px ${alpha(currentTheme.palette.primary.main, 0.12)}`,
                    },
                  },
                }}
              />
            </Stack>
          )}
          {!isDesktop && <MenuMobile isOffset={isOffset} isHome={isHome} navConfig={[...navConfig, ...secondaryNav]} />}
        </Container>
      </ToolbarStyle>

      {isOffset && <ToolbarShadowStyle />}
    </AppBar>
  );
}
